/**
 * Floating ("easy-connect") edge geometry for the flowchart viewer (SCRUM-460).
 *
 * Connectors without an explicit React Flow handle are rendered as floating
 * edges: both endpoints are derived at render time from the relative position
 * of the source and target nodes. Each endpoint is the intersection of the
 * straight line between the two node centers with the node's boundary
 * rectangle, plus the side (`left | right | top | bottom`) that intersection
 * lies on — which feeds straight into `getSmoothStepPath` so the edge keeps
 * the app's step look.
 *
 * Everything in this module is a pure function so it can be unit-tested
 * without React Flow.
 */

export type FloatingSide = 'left' | 'right' | 'top' | 'bottom';

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BoundaryIntersection extends Point {
  side: FloatingSide;
}

export interface FloatingEdgeParams {
  sx: number;
  sy: number;
  sourcePos: FloatingSide;
  tx: number;
  ty: number;
  targetPos: FloatingSide;
}

export const getRectCenter = (rect: Rect): Point => ({
  x: rect.x + rect.width / 2,
  y: rect.y + rect.height / 2,
});

/**
 * Intersection of the ray from `rect`'s center towards `towards` with the
 * rectangle's boundary, plus the side of the rectangle the ray exits through.
 *
 * Side selection ("closest side"): the binding constraint of the ray is the
 * border it reaches first when the offsets are normalized by the half-extents.
 * Perfectly diagonal rays (through a corner) resolve to the horizontal sides
 * (left/right) deterministically.
 *
 * Degenerate cases:
 * - `towards` equals the center: exits through the right side at right-center.
 * - zero-width/height rects: the intersection collapses onto the center line.
 */
export function intersectRectBoundaryTowards(rect: Rect, towards: Point): BoundaryIntersection {
  const center = getRectCenter(rect);
  const dx = towards.x - center.x;
  const dy = towards.y - center.y;

  if (dx === 0 && dy === 0) {
    return { x: center.x + rect.width / 2, y: center.y, side: 'right' };
  }

  const halfWidth = rect.width / 2;
  const halfHeight = rect.height / 2;

  // How many half-extents the ray travels per unit along each axis. The larger
  // value is the border the ray hits first.
  const reachX = halfWidth > 0 ? Math.abs(dx) / halfWidth : (dx === 0 ? 0 : Number.POSITIVE_INFINITY);
  const reachY = halfHeight > 0 ? Math.abs(dy) / halfHeight : (dy === 0 ? 0 : Number.POSITIVE_INFINITY);

  const maxReach = Math.max(reachX, reachY);
  const t = Number.isFinite(maxReach) ? 1 / maxReach : 0;

  const side: FloatingSide =
    reachX >= reachY ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'bottom' : 'top');

  return {
    x: center.x + dx * t,
    y: center.y + dy * t,
    side,
  };
}

/**
 * Floating endpoints + sides for an edge between two node rects.
 *
 * Degenerate overlapping case: when both centers coincide there is no
 * direction to derive anchors from, so the edge deterministically leaves the
 * source through its right side and enters the target through its left side.
 */
export function getFloatingEdgeParams(sourceRect: Rect, targetRect: Rect): FloatingEdgeParams {
  const sourceCenter = getRectCenter(sourceRect);
  const targetCenter = getRectCenter(targetRect);

  if (sourceCenter.x === targetCenter.x && sourceCenter.y === targetCenter.y) {
    return {
      sx: sourceRect.x + sourceRect.width,
      sy: sourceCenter.y,
      sourcePos: 'right',
      tx: targetRect.x,
      ty: targetCenter.y,
      targetPos: 'left',
    };
  }

  const sourceIntersection = intersectRectBoundaryTowards(sourceRect, targetCenter);
  const targetIntersection = intersectRectBoundaryTowards(targetRect, sourceCenter);

  return {
    sx: sourceIntersection.x,
    sy: sourceIntersection.y,
    sourcePos: sourceIntersection.side,
    tx: targetIntersection.x,
    ty: targetIntersection.y,
    targetPos: targetIntersection.side,
  };
}

// ---------------------------------------------------------------------------
// Adapters for React Flow internal nodes (FlowchartViewer)
// ---------------------------------------------------------------------------

/**
 * Oval (start/end) nodes draw an ellipse that is smaller than their node
 * container: the ellipse spans `OVAL_SHAPE_RATIO` of the container minus
 * `OVAL_SHAPE_PADDING` px of padding on each radius. Floating edges should
 * stop at the ellipse, not at the (invisible) container border, so the
 * geometry uses the ellipse's bounding box. Keep in sync with `OvalNode` in
 * `src/components/FlowchartViewer.js` (which derives its shape from this
 * helper).
 */
export const OVAL_SHAPE_RATIO = 0.6;
export const OVAL_SHAPE_PADDING = 10;

export function getOvalVisualRect(rect: Rect): Rect {
  const radiusX = (rect.width * OVAL_SHAPE_RATIO) / 2 - OVAL_SHAPE_PADDING;
  const radiusY = (rect.height * OVAL_SHAPE_RATIO) / 2 - OVAL_SHAPE_PADDING;
  if (radiusX <= 0 || radiusY <= 0) {
    return rect;
  }
  return {
    x: rect.x + rect.width / 2 - radiusX,
    y: rect.y + rect.height / 2 - radiusY,
    width: radiusX * 2,
    height: radiusY * 2,
  };
}

/**
 * Duck-typed subset of React Flow's `InternalNode` (v12). `FlowchartViewer`
 * passes the result of `useInternalNode(...)`, but plain `Node` objects with
 * `position` + `measured`/`width`/`height` work too (handy for tests).
 */
export interface FlowNodeLike {
  type?: string;
  position?: Point;
  internals?: { positionAbsolute?: Point };
  measured?: { width?: number | null; height?: number | null };
  width?: number | null;
  height?: number | null;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Visual bounding rect of a flowchart node in flow coordinates, or `null`
 * when the node has not been measured yet.
 */
export function getFlowNodeRect(node: FlowNodeLike | null | undefined): Rect | null {
  if (!node) return null;

  const position = node.internals?.positionAbsolute ?? node.position;
  const width = node.measured?.width ?? node.width;
  const height = node.measured?.height ?? node.height;

  if (!position || !isFiniteNumber(position.x) || !isFiniteNumber(position.y)) return null;
  if (!isFiniteNumber(width) || !isFiniteNumber(height) || width <= 0 || height <= 0) return null;

  const rect: Rect = { x: position.x, y: position.y, width, height };
  return node.type === 'oval' ? getOvalVisualRect(rect) : rect;
}

/**
 * Floating edge params for two React Flow nodes, or `null` when either node
 * is missing or not measured yet (callers fall back to the standard
 * handle-based path in that case).
 */
export function getFloatingEdgeParamsForNodes(
  sourceNode: FlowNodeLike | null | undefined,
  targetNode: FlowNodeLike | null | undefined
): FloatingEdgeParams | null {
  const sourceRect = getFlowNodeRect(sourceNode);
  const targetRect = getFlowNodeRect(targetNode);
  if (!sourceRect || !targetRect) return null;
  return getFloatingEdgeParams(sourceRect, targetRect);
}
